import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { useListingCMS } from '../../hooks/useListingCMS';
import { useCategories } from '../../hooks/useCategories';
import { useCustomWorkflow } from '../../hooks/useCustomWorkflow';
import DashboardLayout from '../../components/DashboardLayout';
import LiteLayout from '../../components/lite/LiteLayout';
import ErrorBoundary from './ErrorBoundary';
import { getSchema, getDefaultValues } from '../../schemas';

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Progress } from '../../components/ui/progress';
import { Loader2, Save, Eye, EyeOff, ArrowLeft, AlertTriangle, Info, CheckCircle2, AlertCircle, X, Copy } from 'lucide-react';

// Form Field Components
import SchemaCardRenderer from './SchemaCardRenderer';
import CatalogPreviewModal from './CatalogPreviewModal';
import TaxonomyAttributesCard from './TaxonomyAttributesCard';
import FilterAttributesCard from './FilterAttributesCard';
import PublishingCard from './PublishingCard';
import VendorCard from './VendorCard';
import ShippingPricingCard from './ShippingPricingCard';
import AvailabilityScheduler from '../AvailabilityScheduler';

// Complex Module Components (preserved from original)
import ImageUploadPreview from '../../components/ImageUploadPreview';
import VariantManagerV2 from '../../components/VariantManagerV2';
import AttributeRenderer from '../../components/AttributeRenderer';
import WorkflowFieldRenderer from '../../components/WorkflowFieldRenderer';

// Tier Limit Modal for subscription-based publishing
import TierLimitModal from '../../components/vendor/TierLimitModal';

// Logic Utilities
import { validateHSNCode, calculateTax } from '../../logic/taxEngine';

// Service Template Presets
import serviceTemplatePresets from '../../schemas/serviceTemplatePresets.json';
// Rental Template Presets
import rentalTemplatePresets from '../../schemas/rentalTemplatePresets.json';

/**
 * Safe Logic Executor
 * Wraps logic execution to prevent crashes from propagating to UI
 */
const safeExecuteLogic = (fn, defaultValue) => {
  try {
    return fn();
  } catch (error) {
    console.error('Logic execution error:', error);
    return defaultValue;
  }
};

/**
 * UniversalSinglePageForm - Schema-driven 2-column listing form
 * 
 * Supports two operating modes via the `formPurpose` prop:
 *   "listing"           — default: vendor creates / edits a listing (URL-param driven)
 *   "template"          — admin creates a Master Catalogue template; submit saves to
 *                         master_templates, not listings.  Passed via props.
 *   "template-preview"  — read-only render of a template so admin can see vendor view.
 *
 * When rendered in template mode the component can be placed anywhere in the tree
 * (modal, panel, page) — useParams() values are overridden by props.
 */
// Processing & Dispatch Time card — Standard only (hyperlocal has its own dedicated card)
const ProcessingDispatchCard = ({ formData, setFormData }) => (
  <Card data-testid="processing-dispatch-card">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold">Processing & Dispatch Time</CardTitle>
      <p className="text-xs text-gray-500">How long before the order ships after it is placed.</p>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="grid grid-cols-2 gap-4">
        {[
          { key: 'processing_days_min', label: 'Min Days', hint: 'Fastest you can dispatch' },
          { key: 'processing_days_max', label: 'Max Days', hint: 'Slowest you might need' },
        ].map(({ key, label, hint }) => (
          <div key={key} className="space-y-1">
            <label className="text-xs font-medium text-gray-700">{label}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="30"
                value={formData[key] ?? ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  [key]: e.target.value === '' ? null : parseInt(e.target.value, 10),
                }))}
                className="w-20 border rounded-md px-2 py-1.5 text-sm text-gray-900"
                data-testid={`dispatch-${key}`}
              />
              <span className="text-xs text-gray-400">days</span>
            </div>
            <p className="text-[11px] text-gray-400">{hint}</p>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

// Hyperlocal Delivery Prep Time — dedicated dropdown card for same-day listings
const HYPERLOCAL_PREP_OPTIONS = [
  { value: 5,  label: '5 Minutes' },
  { value: 10, label: '10 Minutes' },
  { value: 15, label: '15 Minutes' },
  { value: 20, label: '20 Minutes' },
  { value: 30, label: '30 Minutes' },
  { value: 45, label: '45 Minutes' },
  { value: 60, label: '60 Minutes' },
  { value: 90, label: '90 Minutes' },
  { value: 91, label: '90+ Minutes' },
];

const HyperlocalPrepTimeCard = ({ formData, setFormData }) => (
  <Card data-testid="hyperlocal-prep-time-card">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold">Hyperlocal Delivery Prep Time</CardTitle>
      <p className="text-xs text-gray-500">
        Average time your store needs to prepare an order before it is ready for rider pickup.
        This is the default for all Same-Day orders and can be overridden during order acceptance.
      </p>
    </CardHeader>
    <CardContent className="pt-0">
      <select
        value={formData.hyperlocal_prep_minutes ?? 15}
        onChange={(e) => setFormData(prev => ({
          ...prev,
          hyperlocal_prep_minutes: parseInt(e.target.value, 10),
        }))}
        className="w-full border rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
        data-testid="hyperlocal-prep-minutes-select"
      >
        {HYPERLOCAL_PREP_OPTIONS.map(({ value, label }) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
      <p className="mt-2 text-xs text-gray-500">
        Customers will see: <strong>"Typically delivers within 15–45 mins"</strong>
      </p>
    </CardContent>
  </Card>
);

// Return Claim Eligibility & Evidence card — shown below Return and Cancellation Policy
const RETURN_CLAIM_REASONS = [
  { key: 'damaged',      label: 'Damaged Product',          defaultOn: true },
  { key: 'defective',    label: 'Defective Product',         defaultOn: true },
  { key: 'wrong',        label: 'Wrong Product Delivered',   defaultOn: true },
  { key: 'missing',      label: 'Missing Items',             defaultOn: true },
  { key: 'change_mind',  label: 'Change of Mind',            defaultOn: false },
  { key: 'size_fit',     label: 'Size / Fit Issues',         defaultOn: false },
];

const RETURN_EVIDENCE_OPTIONS = [
  { value: 'none',                label: 'No Evidence Required' },
  { value: 'photos',              label: 'Photos Only' },
  { value: 'video',               label: 'Continuous Unboxing Video (Recommended)' },
  { value: 'photos_video',        label: 'Photos + Continuous Unboxing Video' },
  { value: 'photos_video_verify', label: 'Photos + Continuous Unboxing Video + Vendor Verification' },
];

const ReturnClaimEligibilityCard = ({ formData, setFormData }) => {
  const reasons = formData.return_claim_reasons ?? RETURN_CLAIM_REASONS.filter(r => r.defaultOn).map(r => r.key);
  const evidenceType = formData.return_evidence_type ?? 'video';

  const toggleReason = (key) => {
    setFormData(prev => {
      const current = prev.return_claim_reasons ?? RETURN_CLAIM_REASONS.filter(r => r.defaultOn).map(r => r.key);
      const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
      return { ...prev, return_claim_reasons: next };
    });
  };

  return (
    <Card data-testid="return-claim-eligibility-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">Return Claim Eligibility & Evidence</CardTitle>
        <p className="text-xs text-gray-500">
          Specify which return reasons you accept and what evidence customers must provide.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Claim Reasons */}
        <div>
          <p className="text-xs font-medium text-gray-700 mb-2">Eligible Claim Reasons</p>
          <div className="space-y-2">
            {RETURN_CLAIM_REASONS.map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2.5 cursor-pointer" data-testid={`claim-reason-${key}`}>
                <input
                  type="checkbox"
                  checked={reasons.includes(key)}
                  onChange={() => toggleReason(key)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm text-gray-800">{label}</span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Only selected reasons will be accepted when customers raise a return request.
          </p>
        </div>

        <div className="border-t pt-4">
          <p className="text-xs font-medium text-gray-700 mb-2">Evidence Required for Return Claims</p>
          <select
            value={evidenceType}
            onChange={(e) => setFormData(prev => ({ ...prev, return_evidence_type: e.target.value }))}
            className="w-full border rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
            data-testid="return-evidence-select"
          >
            {RETURN_EVIDENCE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <div className="mt-2 space-y-1 text-xs text-gray-500">
            {evidenceType === 'photos' && <p>Customer must upload clear images supporting the claim.</p>}
            {(evidenceType === 'video' || evidenceType === 'photos_video' || evidenceType === 'photos_video_verify') && (
              <p>A single uninterrupted video showing the sealed package, label, unboxing, and the reported issue is required.</p>
            )}
            {evidenceType === 'photos_video_verify' && (
              <p className="text-orange-700 bg-orange-50 rounded px-2 py-1">After evidence is submitted, the claim stays pending until you review and approve or reject it.</p>
            )}
          </div>
        </div>

        <div className="border-t pt-3 rounded-md bg-gray-50 p-3">
          <p className="text-xs text-gray-600">
            <strong>Perishable / Expiry-dated products</strong> are automatically non-returnable after delivery
            unless the claim is for a damaged, defective, incorrect, or missing item.
            Platform admins may intervene in exceptional cases per marketplace policy.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};



const UniversalSinglePageForm = ({
  // Template-mode props (all optional; absent = normal listing mode)
  formPurpose = "listing",        // "listing" | "template" | "template-preview"
  categoryOverride,               // schema category key (e.g. "service", "rental")
  initialData,                    // pre-populated default field values (from template adopt or edit)
  templateId,                     // set when editing an existing template
  templateMeta,                   // { templateName, templateDescription, templateImage, templatePriority }
  onTemplateSave,                 // callback(savedDoc) after template is saved
  onTemplateCancel,               // callback when admin clicks Cancel in template mode
  noLayout = false,               // when true, skip DashboardLayout wrapper (parent provides layout)
} = {}) => {
  const { category: categoryParam, listingId: listingIdParam } = useParams();
  const isTemplateMode    = formPurpose === "template";
  const isTemplatePreview = formPurpose === "template-preview";

  // Template setup state — drives listing type (schema) and template identity fields
  const [templateSetup, setTemplateSetup] = useState({
    listingType: categoryOverride || 'service',
    templateName: templateMeta?.templateName || '',
    templateDescription: templateMeta?.templateDescription || '',
    templatePriority: templateMeta?.templatePriority || 1,
  });

  // In template mode the listing type is editable; otherwise use override or URL param
  const category = isTemplateMode ? (templateSetup.listingType || categoryOverride || 'service')
                                  : (categoryOverride || categoryParam);
  const listingId = formPurpose === "listing" ? listingIdParam : null;
  const navigate = useNavigate();
  const { user, apiCall } = useAuth();
  const { toast } = useToast();
  
  // Mobile detection for Lite Layout
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Get schema for this category
  const schema = useMemo(() => getSchema(category), [category]);
  
  // Hooks
  const cms = useListingCMS(listingId, schema?.listingType);
  const { 
    categories, 
    getSubcategories, 
    getCategoryNames,
    getSubcategoryNames,
    getCategoryByName,
    getSubcategoryByName,
    loading: categoriesLoading,
    refetch: fetchCategories 
  } = useCategories(schema?.categoryType || category);
  
  // Check user's KYC tier for default values
  const getUserKycTier = useCallback(() => {
    // Admins are always tier3+
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      return 'tier3';
    }
    // Check vendor profile KYC status
    const kycLevel = user?.kyc_level || user?.vendor_profile?.kyc_level;
    if (kycLevel >= 2) return 'tier2';
    if (kycLevel >= 3) return 'tier3';
    return 'default';
  }, [user]);
  
  // Get KYC-based default values
  const getKycBasedDefaults = useCallback(() => {
    if (!schema?.kycBasedDefaults) return {};
    
    const tier = getUserKycTier();
    const defaults = {};
    
    Object.entries(schema.kycBasedDefaults).forEach(([field, tiers]) => {
      defaults[field] = tiers[tier] || tiers.default;
    });
    
    return defaults;
  }, [schema, getUserKycTier]);

  // Subcategory → service template/bookingMode/pricingModel/unitType and more defaults
  const getServiceTemplateDefaults = useCallback((subcategory) => {
    if (!subcategory) return null;
    const key = subcategory.toLowerCase().trim();
    const presets = serviceTemplatePresets.subcategoryPresets || {};

    // Exact match first
    if (presets[key]) return presets[key];

    // Partial match fallback (handles slight name variations)
    for (const [mapKey, defaults] of Object.entries(presets)) {
      if (key.includes(mapKey) || mapKey.includes(key)) return defaults;
    }
    return null;
  }, []);

  // Template → field-level defaults (used when vendor manually changes template)
  const getTemplateFieldDefaults = useCallback((template) => {
    return serviceTemplatePresets.templateDefaults?.[template] || null;
  }, []);

  // Rental subcategory → rentalTemplate/pricingModel/etc defaults
  const getRentalTemplateDefaults = useCallback((subcategory) => {
    if (!subcategory) return null;
    const key = subcategory.toLowerCase().trim();
    const presets = rentalTemplatePresets.subcategoryPresets || {};
    if (presets[key]) return presets[key];
    for (const [mapKey, defaults] of Object.entries(presets)) {
      if (key.includes(mapKey) || mapKey.includes(key)) return defaults;
    }
    return null;
  }, []);

  // Rental template → field-level defaults (used when vendor manually changes rentalTemplate)
  const getRentalTemplateFieldDefaults = useCallback((template) => {
    return rentalTemplatePresets.templateDefaults?.[template] || null;
  }, []);
  
  // State
  const [loading, setLoading] = useState(false);
  const [showCatalogPreview, setShowCatalogPreview] = useState(false);
  const [formData, setFormData] = useState(() => ({
    ...getDefaultValues(category),
    ...getKycBasedDefaults(), // Apply KYC-based defaults
    vendor_id: user?.id,
    vendorHasManualPricing: false,
    manualPricingForProduct: false,
    delivery_modes: ['domestic'],  // default — vendor can change per listing
    processing_days_min: 1,        // Standard tab: min dispatch days
    processing_days_max: 3,        // Standard tab: max dispatch days
    hyperlocal_prep_minutes: 15,   // Hyperlocal tab: prep time before rider pickup
    hyperlocal_availability: null, // Hyperlocal tab: AvailabilityScheduler output
    return_claim_reasons: ['damaged', 'defective', 'wrong', 'missing'], // Return claim eligibility
    return_evidence_type: 'video', // Evidence required for claims
  }));
  const [errors, setErrors] = useState({});
  const [taxonomyAttributes, setTaxonomyAttributes] = useState({});
  const [filterAttributes, setFilterAttributes] = useState({});
  const [autoSaveStatus, setAutoSaveStatus] = useState({ saving: false, lastSaved: null });
  const [currentSubcategories, setCurrentSubcategories] = useState([]);
  const [presetsAppliedFor, setPresetsAppliedFor] = useState(null);
  const autoSaveTimerRef = useRef(null);
  
  // MRP Compliance modal state
  const [mrpModal, setMrpModal] = useState({ open: false, basePrice: 0, bumpedPrice: 0, mrpPrice: 0 });

  // Shiprocket Quick city eligibility (fetched once for vendor non-template mode)
  const [quickCityEligibility, setQuickCityEligibility] = useState({ eligible: true, city: '', message: '', checked: false });

  // Tier limit modal state for subscription-based publishing
  const [tierLimitModal, setTierLimitModal] = useState({
    open: false,
    errorType: null,
    message: '',
    options: {},
    currentTypes: [],
    newType: null,
    typesLimit: 0,
    currentCount: 0,
    limit: 0
  });
  
  // Custom workflow (for service category primarily)
  const customWorkflow = useCustomWorkflow(
    schema?.listingType,
    formData[schema?.categoryField] || formData.category,
    formData[schema?.subcategoryField] || formData.subcategory
  );

  // iter-347 — Category/subcategory compliance config (Country of Origin is
  // universal; Licenses/Shelf-Life/Warranty rules come from the CMS)
  const requiredLicenseType = useMemo(() => {
    const categoryName = formData[schema?.categoryField] || formData.category;
    const subcategoryName = formData[schema?.subcategoryField] || formData.subcategory;
    const selectedCat = getCategoryByName?.(categoryName);
    const selectedSub = getSubcategoryByName?.(categoryName, subcategoryName);
    return selectedSub?.required_license_type || selectedCat?.required_license_type || null;
  }, [formData[schema?.categoryField], formData.category, formData[schema?.subcategoryField], formData.subcategory, getCategoryByName, getSubcategoryByName]);

  const complianceConfig = useMemo(() => {
    const categoryName = formData[schema?.categoryField] || formData.category;
    const subcategoryName = formData[schema?.subcategoryField] || formData.subcategory;
    const selectedCat = getCategoryByName?.(categoryName);
    const selectedSub = getSubcategoryByName?.(categoryName, subcategoryName);
    const pick = (field) => (selectedSub?.[field] ?? selectedCat?.[field]);
    return {
      enable_expiry_rules: !!pick('enable_expiry_rules'),
      expiry_date_format: pick('expiry_date_format') || 'shelf_life',
      expiry_legal_label: pick('expiry_legal_label') || 'Best Before',
      min_guaranteed_shelf_life_days: pick('min_guaranteed_shelf_life_days') || null,
      min_guaranteed_shelf_life_percent: pick('min_guaranteed_shelf_life_percent') || null,
      enable_warranty_rules: !!pick('enable_warranty_rules'),
      default_assurance_type: pick('default_assurance_type') || 'manufacturer_warranty',
      default_warranty_duration_unit: pick('default_warranty_duration_unit') || 'months',
      // iter-350 — Admin/Super Admin Buying Mode Override (temporary
      // compliance hold, e.g. Food/Medicines/Consumables pending platform
      // license). Subcategory value wins over category, same pattern as
      // every other compliance toggle above.
      buying_mode_override_enabled: !!pick('buying_mode_override_enabled'),
      buying_mode_override_type: pick('buying_mode_override_type') || 'enquiry',
    };
  }, [formData[schema?.categoryField], formData.category, formData[schema?.subcategoryField], formData.subcategory, getCategoryByName, getSubcategoryByName]);

  // Effective freshness-guarantee floor = whichever of the fixed-days or
  // percent-of-shelf-life threshold is greater for this listing's own total shelf life
  const shelfLifeFloorDays = useMemo(() => {
    const totalDays = parseInt(formData.shelf_life_days, 10);
    const fixed = complianceConfig.min_guaranteed_shelf_life_days;
    const pct = complianceConfig.min_guaranteed_shelf_life_percent;
    const pctFloor = pct && !isNaN(totalDays) ? Math.round((totalDays * pct) / 100) : null;
    const candidates = [fixed, pctFloor].filter((v) => v);
    return candidates.length ? Math.max(...candidates) : null;
  }, [formData.shelf_life_days, complianceConfig]);

  const shelfLifeGuaranteeWording = useMemo(() => {
    const days = complianceConfig.min_guaranteed_shelf_life_days;
    const pct = complianceConfig.min_guaranteed_shelf_life_percent;
    const parts = [];
    if (days) parts.push(`${days} days`);
    if (pct) parts.push(`${pct}% of the Total Shelf Life`);
    if (!parts.length) return null;
    if (parts.length === 1) return `at least ${parts[0]}`;
    return `at least ${parts[0]} OR ${parts[1]}, whichever is greater`;
  }, [complianceConfig]);

  // Fetch existing listing data if editing
  useEffect(() => {
    if (listingId && schema) {
      fetchListing();
    }
  }, [listingId, schema]);

  // ============ AUTO-REPLICATE: Load vendor defaults for new listings ============
  useEffect(() => {
    if (listingId) return; // Only for new listings
    if (!schema?.listingType) return;

    try {
      const type = schema.listingType;
      // Universal cross-type baseline (lowest priority)
      const universal = JSON.parse(localStorage.getItem('vendor_universal_policies') || '{}');

      if (type === 'product') {
        const saved = JSON.parse(localStorage.getItem('vendor_product_defaults') || '{}');
        if (Object.keys(saved).length > 0) {
          setFormData(prev => ({
            ...prev,
            ...(prev.serviceableType === 'country' || !prev.serviceableType ? { serviceableType: saved.serviceableType || prev.serviceableType } : {}),
            ...((!prev.serviceableStates || prev.serviceableStates.length === 0) ? { serviceableStates: saved.serviceableStates || [] } : {}),
            cancellationAllowed: saved.cancellationAllowed ?? universal.cancellation_allowed ?? prev.cancellationAllowed,
            cancellationWindow: saved.cancellationWindow || universal.cancellation_window || prev.cancellationWindow,
            cancellationTiming: saved.cancellationTiming || prev.cancellationTiming,
            returnPolicy: saved.returnPolicy || prev.returnPolicy,
            returnExchangeWindow: saved.returnExchangeWindow || prev.returnExchangeWindow,
          }));
        }
      } else if (type === 'service') {
        const saved = JSON.parse(localStorage.getItem('vendor_service_defaults') || '{}');
        setFormData(prev => ({
          ...prev,
          ...(saved.cancellationAllowed !== undefined ? { cancellationAllowed: saved.cancellationAllowed } : universal.cancellation_allowed !== undefined ? { cancellationAllowed: universal.cancellation_allowed } : {}),
          ...(saved.cancellationWindow ? { cancellationWindow: saved.cancellationWindow } : universal.cancellation_window ? { cancellationWindow: universal.cancellation_window } : {}),
          ...(saved.cancellationTiming ? { cancellationTiming: saved.cancellationTiming } : {}),
          ...(saved.terms ? { terms: saved.terms } : universal.terms ? { terms: universal.terms } : {}),
          ...(saved.cancellationPolicyDetails ? { cancellationPolicyDetails: saved.cancellationPolicyDetails } : universal.cancellation_details ? { cancellationPolicyDetails: universal.cancellation_details } : {}),
          ...(saved.serviceGuarantee ? { serviceGuarantee: saved.serviceGuarantee } : {}),
        }));
      } else if (type === 'rental') {
        const saved = JSON.parse(localStorage.getItem('vendor_rental_defaults') || '{}');
        setFormData(prev => ({
          ...prev,
          ...(saved.cancellationAllowed !== undefined ? { cancellationAllowed: saved.cancellationAllowed } : universal.cancellation_allowed !== undefined ? { cancellationAllowed: universal.cancellation_allowed } : {}),
          ...(saved.cancellationWindow ? { cancellationWindow: saved.cancellationWindow } : universal.cancellation_window ? { cancellationWindow: universal.cancellation_window } : {}),
          ...(saved.terms ? { terms: saved.terms } : universal.terms ? { terms: universal.terms } : {}),
          ...(saved.damage_policy ? { damagePolicy: saved.damage_policy } : {}),
        }));
      } else if (type === 'event') {
        const saved = JSON.parse(localStorage.getItem('vendor_event_defaults') || '{}');
        setFormData(prev => ({
          ...prev,
          ...(saved.cancellationAllowed !== undefined ? { cancellationAllowed: saved.cancellationAllowed } : universal.cancellation_allowed !== undefined ? { cancellationAllowed: universal.cancellation_allowed } : {}),
          ...(saved.cancellationWindow ? { cancellationWindow: saved.cancellationWindow } : universal.cancellation_window ? { cancellationWindow: universal.cancellation_window } : {}),
          ...(saved.terms ? { terms: saved.terms } : universal.terms ? { terms: universal.terms } : {}),
        }));
      } else if (type === 'subscription') {
        const saved = JSON.parse(localStorage.getItem('vendor_subscription_defaults') || '{}');
        setFormData(prev => ({
          ...prev,
          ...(saved.cancellationAllowed !== undefined ? { cancellationAllowed: saved.cancellationAllowed } : universal.cancellation_allowed !== undefined ? { cancellationAllowed: universal.cancellation_allowed } : {}),
          ...(saved.cancellationWindow ? { cancellationWindow: saved.cancellationWindow } : universal.cancellation_window ? { cancellationWindow: universal.cancellation_window } : {}),
          ...(saved.terms ? { terms: saved.terms } : universal.terms ? { terms: universal.terms } : {}),
        }));
      }
    } catch (e) { /* ignore parse errors */ }
  }, [listingId, schema?.listingType]);

  // iter-349 — Pre-fill previously-entered license numbers (per type) for
  // NEW listings only, so vendors don't have to re-type/re-locate them.
  const [defaultLicenses, setDefaultLicenses] = useState({});
  useEffect(() => {
    if (listingId) return; // only for new listings; edits show the listing's own saved values
    (async () => {
      try {
        const res = await apiCall('/api/listings/defaults/licenses');
        setDefaultLicenses(res?.default_licenses || {});
      } catch (e) { /* non-critical convenience fetch */ }
    })();
  }, [listingId]);
  
  // Check if vendor has manual pricing enabled
  useEffect(() => {
    const checkVendorPricingConfig = async () => {
      if (!user?.id) return;
      
      try {
        const config = await apiCall(`/api/product-option-templates/system/pricing-config?vendor_id=${user.id}`);
        if (config.override_source === 'vendor') {
          setFormData(prev => ({
            ...prev,
            vendorHasManualPricing: true
          }));
        }
      } catch (e) {
        // Ignore errors - default to automatic pricing
      }
    };
    
    checkVendorPricingConfig();
  }, [user?.id]);
  
  // Fetch categories on mount
  useEffect(() => {
    if (schema?.usesCategories && fetchCategories) {
      fetchCategories();
    }
  }, [schema]);

  // Shiprocket Quick city eligibility — fetch once for vendors in listing mode
  useEffect(() => {
    if (isTemplateMode || isTemplatePreview) return;
    const isVendor = user?.role !== 'admin' && user?.role !== 'super_admin';
    if (!isVendor) return;

    apiCall('/api/shipping/hyperlocal/city-eligibility')
      .then((res) => {
        setQuickCityEligibility({ eligible: res.eligible, city: res.city, message: res.message, checked: true });
      })
      .catch(() => {
        // On error, optimistically allow (city may not be set yet)
        setQuickCityEligibility({ eligible: true, city: '', message: '', checked: true });
      });
  }, [isTemplateMode, isTemplatePreview, user?.id]); // apiCall is stable  
  // Load subcategories once categories are loaded and we have a category selected
  // This handles the case where listing loads before categories (and the edit-form preservation fix)
  useEffect(() => {
    const categoryName = formData[schema?.categoryField] || formData.category;
    if (categoryName && schema?.usesSubcategory && categories?.length > 0) {
      const subcats = getSubcategoryNames(categoryName);
      if (subcats.length > 0) {
        setCurrentSubcategories(subcats);
      }
    }
  }, [categories?.length, formData[schema?.categoryField], formData.category, schema?.categoryField]);
  
  // Auto-dismiss the "presets applied" banner after 5 seconds
  useEffect(() => {
    if (!presetsAppliedFor) return;
    const timer = setTimeout(() => setPresetsAppliedFor(null), 5000);
    return () => clearTimeout(timer);
  }, [presetsAppliedFor]);

  // Auto-save effect (existing listings)
  useEffect(() => {
    if (!listingId || !formData.title || !schema?.features?.hasAutoSave) return;
    
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      triggerAutoSave();
    }, 3000);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData, listingId, schema]);

  // localStorage draft auto-save for NEW listings (no listingId yet)
  const localDraftKey = categoryParam ? `vendor_listing_draft_${categoryParam}` : null;
  useEffect(() => {
    if (listingId || !formData.title || !localDraftKey) return; // only for new, non-empty forms
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(localDraftKey, JSON.stringify({ formData, savedAt: new Date().toISOString() }));
        setAutoSaveStatus({ saving: false, lastSaved: new Date(), error: null });
      } catch (e) { /* storage quota exceeded — silently skip */ }
    }, 3000);
    return () => clearTimeout(timer);
  }, [formData, listingId, localDraftKey]);

  // Restore localStorage draft on new-listing mount
  const [localDraft, setLocalDraft] = useState(null);
  useEffect(() => {
    if (listingId || !localDraftKey) return;
    try {
      const raw = localStorage.getItem(localDraftKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.formData?.title) setLocalDraft(parsed);
      }
    } catch (e) { /* corrupt draft — ignore */ }
  }, [listingId, localDraftKey]); // run once on mount

  const restoreLocalDraft = () => {
    if (!localDraft) return;
    setFormData(prev => ({ ...prev, ...localDraft.formData }));
    setLocalDraft(null);
    localStorage.removeItem(localDraftKey);
  };

  const discardLocalDraft = () => {
    setLocalDraft(null);
    localStorage.removeItem(localDraftKey);
  };

  // beforeunload warning for unsaved new listings
  useEffect(() => {
    if (listingId) return; // editing saves via autosave, no need
    const handleBeforeUnload = (e) => {
      if (!formData.title) return;
      e.preventDefault();
      e.returnValue = 'You have unsaved listing data. Are you sure you want to leave?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData.title, listingId]);

  // When initialData is provided (template adopt or template edit), merge into form state
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      // Flatten nested 'data' object back to top-level (template save nests form fields inside data:{})
      const { data: nestedData, ...topLevel } = initialData;
      const flattened = { ...topLevel };
      if (nestedData && typeof nestedData === 'object') {
        Object.entries(nestedData).forEach(([key, value]) => {
          // Don't overwrite top-level fields that already exist
          if (flattened[key] === undefined || flattened[key] === null || flattened[key] === '') {
            flattened[key] = value;
          }
        });
      }
      // Auto-derive hasVariants from actual variant data
      const vc = flattened.variantCombinations || flattened.variant_combinations || [];
      if (vc.length > 0 && !flattened.hasVariants) {
        flattened.hasVariants = true;
        flattened.variantCombinations = vc;
      }
      setFormData(prev => ({ ...prev, ...flattened }));
    }
  }, [initialData]);

  const fetchListing = async () => {
    setLoading(true);
    try {
      const data = await cms.fetchListing();
      if (data) {
        const nestedData = data.data || {};
        
        // Get category and subcategory names
        const categoryName = data.category || nestedData[schema.categoryField] || '';
        const subcategoryName = data.subcategory || nestedData[schema.subcategoryField] || '';
        
        // Look up categoryId and subcategoryId if not present
        let categoryId = data.categoryId || nestedData.categoryId || '';
        let subcategoryId = data.subcategoryId || nestedData.subcategoryId || '';
        
        // If IDs are not present but names are, look them up
        if (!categoryId && categoryName) {
          const categoryObj = getCategoryByName(categoryName);
          if (categoryObj) {
            categoryId = categoryObj.id;
          }
        }
        
        if (!subcategoryId && subcategoryName && categoryName) {
          const subcategoryObj = getSubcategoryByName(categoryName, subcategoryName);
          if (subcategoryObj) {
            subcategoryId = subcategoryObj.id;
          }
        }
        
        setFormData(prev => ({
          ...prev,
          ...data,
          ...nestedData,
          [schema.categoryField]: categoryName,
          [schema.subcategoryField || 'subcategory']: subcategoryName,
          categoryId: categoryId,
          subcategoryId: subcategoryId,
          subcategory: subcategoryName, // Also set explicit subcategory field
          media: data.media || [],
          variants: data.variants || nestedData.variants || [],
          // Handle both camelCase and snake_case for variant combinations
          variantCombinations: data.variantCombinations || data.variant_combinations || nestedData.variantCombinations || nestedData.variant_combinations || [],
          // Auto-derive hasVariants from actual variant data if not explicitly set
          hasVariants: nestedData.hasVariants || data.hasVariants || 
            !!(data.variant_combinations?.length || data.variantCombinations?.length || nestedData.variantCombinations?.length),
          // Map snake_case backend fields to camelCase form fields
          template: data.template || nestedData.template || prev.template,
          bookingMode: data.booking_mode || data.bookingMode || nestedData.bookingMode || prev.bookingMode,
          pricingModel: data.pricing_model || data.pricingModel || nestedData.pricingModel || prev.pricingModel,
          status: data.status || 'draft',
          revision_status: data.revision_status,
          // iter-347 — flatten top-level compliance fields into form fields
          country_of_origin: data.country_of_origin ?? nestedData.country_of_origin ?? prev.country_of_origin,
          licenses_certifications: data.licenses_certifications || nestedData.licenses_certifications || prev.licenses_certifications || [],
          excluded_license_types: data.excluded_license_types || nestedData.excluded_license_types || prev.excluded_license_types || [],
          net_quantity_value: data.net_quantity?.value ?? nestedData.net_quantity?.value ?? prev.net_quantity_value,
          net_quantity_unit: data.net_quantity?.unit ?? nestedData.net_quantity?.unit ?? prev.net_quantity_unit,
          shelf_life_days: data.shelf_life?.shelf_life_days ?? nestedData.shelf_life?.shelf_life_days ?? prev.shelf_life_days,
          expiry_date: data.shelf_life?.expiry_date ?? nestedData.shelf_life?.expiry_date ?? prev.expiry_date,
          freshness_guarantee_days: data.shelf_life?.min_freshness_guarantee_days ?? nestedData.shelf_life?.min_freshness_guarantee_days ?? prev.freshness_guarantee_days,
          warranty_assurance_type: data.warranty?.assurance_type ?? nestedData.warranty?.assurance_type ?? prev.warranty_assurance_type,
          warranty_duration_value: data.warranty?.duration_value ?? nestedData.warranty?.duration_value ?? prev.warranty_duration_value,
          warranty_duration_unit: data.warranty?.duration_unit ?? nestedData.warranty?.duration_unit ?? prev.warranty_duration_unit,
          // Auto-compute subscription_type from category mapping (schema-driven)
          ...(schema?.subscriptionTypeMapping && categoryName ? {
            subscription_type: schema.subscriptionTypeMapping[categoryName] || nestedData.subscription_type || 'physical'
          } : {}),
        }));
        
        // IMPORTANT: Load subcategories for the current category
        // This must happen after setFormData so the dropdown shows the correct options
        if (categoryName && schema?.usesSubcategory) {
          const subcats = getSubcategoryNames(categoryName);
          setCurrentSubcategories(subcats);
        }
        
        // Set taxonomy attributes if present
        if (nestedData.taxonomyAttributes || data.taxonomyAttributes) {
          setTaxonomyAttributes(nestedData.taxonomyAttributes || data.taxonomyAttributes || {});
        }
        if (nestedData.filter_attributes || data.filter_attributes) {
          setFilterAttributes(nestedData.filter_attributes || data.filter_attributes || {});
        }
        // Ensure delivery_modes is populated for legacy listings (default to domestic)
        setFormData(prev => ({
          ...prev,
          delivery_modes: prev.delivery_modes?.length
            ? prev.delivery_modes
            : (nestedData.delivery_modes || data.delivery_modes || ['domestic']),
        }));
      }
    } catch (error) {
      console.error('Error fetching listing:', error);
      toast({
        title: 'Error',
        description: 'Failed to load listing data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Trigger auto-save
   */
  const triggerAutoSave = async () => {
    if (!listingId) return;
    
    setAutoSaveStatus({ saving: true, lastSaved: null });
    try {
      await cms.triggerAutoSave(prepareSubmissionData());
      setAutoSaveStatus({ saving: false, lastSaved: new Date() });
    } catch (error) {
      setAutoSaveStatus({ saving: false, lastSaved: null });
    }
  };

  /**
   * Duplicate current listing — creates a draft copy and opens it in edit mode.
   * Only available when editing an existing listing (listingId is truthy).
   */
  const handleDuplicate = async () => {
    if (!listingId) return;
    try {
      const newListing = await apiCall(`/api/listings/${listingId}/duplicate`, { method: 'POST' });
      if (newListing?.id) {
        toast({ title: 'Listing duplicated', description: `"${newListing.title}" saved as draft.` });
        navigate(`/edit-listing/${schema?.listingType}/${newListing.id}`);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to duplicate listing', variant: 'destructive' });
    }
  };

  const handleDeactivate = async () => {
    if (!listingId) return;
    setLoading(true);
    try {
      await apiCall(`/api/listings/${listingId}/deactivate`, { method: 'POST' });
      setFormData(prev => ({ ...prev, status: 'inactive' }));
      cms.setCurrentListing(prev => prev ? { ...prev, status: 'inactive' } : prev);
      toast({ title: 'Listing deactivated', description: 'Hidden from customers. Reactivate it anytime — nothing was deleted.' });
    } catch (error) {
      toast({ title: 'Error', description: error?.message || 'Failed to deactivate listing', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!listingId) return;
    setLoading(true);
    try {
      await apiCall(`/api/listings/${listingId}/reactivate`, { method: 'POST' });
      setFormData(prev => ({ ...prev, status: 'active' }));
      cms.setCurrentListing(prev => prev ? { ...prev, status: 'active' } : prev);
      toast({ title: 'Listing reactivated', description: 'Live again — no re-approval needed.' });
    } catch (error) {
      toast({ title: 'Error', description: error?.message || 'Failed to reactivate listing', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Handle field change
   */
  const handleChange = useCallback((field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Handle category change - load subcategories and store categoryId
      if (field === schema?.categoryField && schema?.usesSubcategory) {
        const subcats = getSubcategoryNames(value);
        setCurrentSubcategories(subcats);
        newData[schema.subcategoryField] = '';
        newData.subcategoryId = ''; // Clear subcategoryId when category changes
        
        // Store categoryId from the category object
        const categoryObj = getCategoryByName(value);
        if (categoryObj) {
          newData.categoryId = categoryObj.id;
        }
      }

      // Auto-compute subscription_type from category (schema-driven mapping)
      if (field === schema?.categoryField && schema?.subscriptionTypeMapping) {
        const subscriptionType = schema.subscriptionTypeMapping[value] || 'physical';
        const prevSubscriptionType = prev.subscription_type || 'physical';
        newData.subscription_type = subscriptionType;
        // Clear unit_type when subscription type changes (options set changes)
        if (subscriptionType !== prevSubscriptionType) {
          newData.unit_type = '';
        }
      }
      
      // Handle subcategory change - store subcategoryId
      if (field === schema?.subcategoryField) {
        const categoryName = prev[schema?.categoryField];
        const subcategoryObj = getSubcategoryByName(categoryName, value);
        if (subcategoryObj) {
          newData.subcategoryId = subcategoryObj.id;
        }
        
        // Auto-set template, bookingMode, pricingModel defaults for services
        if (schema?.listingType === 'service' && value) {
          const preset = getServiceTemplateDefaults(value);
          if (preset) {
            // Always set core service-type identifiers from subcategory preset
            if (preset.template) newData.template = preset.template;
            if (preset.bookingMode) newData.bookingMode = preset.bookingMode;
            if (preset.pricingModel) newData.pricingModel = preset.pricingModel;
            if (preset.locationType) newData.locationType = preset.locationType;

            // Apply secondary fields only if not already filled by vendor
            Object.entries(preset).forEach(([key, presetValue]) => {
              if (['template', 'bookingMode', 'pricingModel', 'locationType'].includes(key)) return;
              if (presetValue !== null && presetValue !== undefined &&
                  (prev[key] === undefined || prev[key] === '' || prev[key] === null)) {
                newData[key] = presetValue;
              }
            });

            // Signal preset was applied (auto-clears after 5s via useEffect)
            setTimeout(() => setPresetsAppliedFor(value), 0);
          }
        }

        // Auto-set rentalTemplate/pricingModel defaults for rentals
        if (schema?.listingType === 'rental' && value) {
          const preset = getRentalTemplateDefaults(value);
          if (preset) {
            // Always set core rental-type identifiers
            if (preset.rentalTemplate) newData.rentalTemplate = preset.rentalTemplate;
            if (preset.pricingModel) newData.pricingModel = preset.pricingModel;

            // Apply secondary fields only if empty
            Object.entries(preset).forEach(([key, presetValue]) => {
              if (['rentalTemplate', 'pricingModel'].includes(key)) return;
              if (presetValue !== null && presetValue !== undefined &&
                  (prev[key] === undefined || prev[key] === '' || prev[key] === null)) {
                newData[key] = presetValue;
              }
            });

            setTimeout(() => setPresetsAppliedFor(value), 0);
          }
        }
      }
      
      // When template changes, auto-set field defaults for any empty fields
      if (field === 'template') {
        const templateDefaults = getTemplateFieldDefaults(value);
        if (templateDefaults) {
          Object.entries(templateDefaults).forEach(([key, defaultValue]) => {
            if (defaultValue !== null && defaultValue !== undefined &&
                (prev[key] === undefined || prev[key] === '' || prev[key] === null)) {
              newData[key] = defaultValue;
            }
          });
        }
      }

      // When rentalTemplate changes, auto-set field defaults for any empty fields
      if (field === 'rentalTemplate') {
        const rentalDefaults = getRentalTemplateFieldDefaults(value);
        if (rentalDefaults) {
          Object.entries(rentalDefaults).forEach(([key, defaultValue]) => {
            if (defaultValue !== null && defaultValue !== undefined &&
                (prev[key] === undefined || prev[key] === '' || prev[key] === null)) {
              newData[key] = defaultValue;
            }
          });
        }
      }
      
      // Handle product type change (physical <-> digital)
      if (field === 'productType') {
        if (value === 'digital') {
          // Clear physical product fields
          newData.requiresShipping = false;
          newData.weight = '';
          newData.length = '';
          newData.width = '';
          newData.height = '';
          newData.serviceableType = '';
          newData.serviceableStates = [];
          newData.serviceablePincodes = '';
          // Set default digital delivery method
          newData.digitalDeliveryMethod = ['email', 'storefront'];
        } else if (value === 'physical') {
          // Clear digital product fields
          newData.digitalDownloadUrl = '';
          newData.digitalFileType = '';
          newData.digitalFileSize = '';
          newData.digitalDeliveryMethod = [];
          newData.digitalAccessDuration = '';
          newData.digitalMaxDownloads = '';
          newData.digitalInstructions = '';
          // Set default physical delivery options
          newData.requiresShipping = true;
          newData.serviceableType = 'country';
        }
      }
      
      // Sync bookingMode <-> booking_type (service listing form)
      // bookingMode options: instant | request | enquiry
      // booking_type options: booking | request | enquiry
      if (field === 'bookingMode') {
        const modeToType = { instant: 'booking', request: 'request', enquiry: 'enquiry' };
        newData.booking_type = modeToType[value] ?? value;
      }
      if (field === 'booking_type') {
        const typeToMode = { booking: 'instant', request: 'request', enquiry: 'enquiry' };
        newData.bookingMode = typeToMode[value] ?? value;
      }

      // Handle HSN code validation
      if (field === 'hsnCode' || field === 'hsn_sac_code') {
        const validation = validateHSNCode(value);
        if (!validation.valid && value) {
          setErrors(prev => ({ ...prev, [field]: validation.error }));
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
          });
        }
      }
      
      return newData;
    });
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [schema, errors, getSubcategoryNames, getCategoryByName, getSubcategoryByName, getServiceTemplateDefaults, getTemplateFieldDefaults, getRentalTemplateDefaults, getRentalTemplateFieldDefaults]);
  
  /**
   * Handle taxonomy attribute change
   */
  const handleTaxonomyAttributeChange = useCallback((key, value) => {
    setTaxonomyAttributes(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);
  
  /**
   * Prepare data for submission
   */
  const prepareSubmissionData = useCallback(() => {
    const categoryField = schema?.categoryField || 'category';
    const categoryValue = formData[categoryField];
    
    // Build data object with type-specific fields
    const dataObj = { ...formData };
    
    // Remove top-level fields that go in the main listing
    delete dataObj.listing_type;
    delete dataObj.title;
    delete dataObj.description;
    delete dataObj.category;
    delete dataObj.media;
    delete dataObj.variants;
    delete dataObj.variantCombinations;
    delete dataObj.status;
    // Catalog Architecture v7 Phase F1 — `catalog_mode` is a top-level
    // field on the backend's ListingCreate model (see models/listing.py),
    // not a `data.*` field. It was previously left in `dataObj` and never
    // surfaced on the returned payload below, so the backend always saw
    // catalog_mode=null regardless of what the vendor picked — pre-existing
    // gap since Phase 1 (iter-386), now fixed since F1's collision-safe
    // title allocator depends on reading the real catalog_mode value.
    delete dataObj.catalog_mode;
    delete dataObj.revision_status;
    // vendor_id belongs at top-level, not nested inside data
    delete dataObj.vendor_id;
    delete dataObj.vendor_name;
    delete dataObj.secondary_categories;
    // iter-347 — compliance fields are sent as dedicated top-level fields below
    delete dataObj.country_of_origin;
    delete dataObj.licenses_certifications;
    delete dataObj.net_quantity_value;
    delete dataObj.net_quantity_unit;
    delete dataObj.shelf_life_days;
    delete dataObj.expiry_date;
    delete dataObj.freshness_guarantee_days;
    delete dataObj.warranty_assurance_type;
    delete dataObj.warranty_duration_value;
    delete dataObj.warranty_duration_unit;
    
    // Add taxonomy attributes
    dataObj.taxonomyAttributes = taxonomyAttributes;
    // Add filter attributes (Shopify metafields for Search & Discovery)
    dataObj.filter_attributes = filterAttributes;
    
    return {
      listing_type: schema?.listingType,
      title: formData.title,
      description: formData.description,
      category: categoryValue,
      subcategory: formData[schema?.subcategoryField] || formData.subcategory || null,
      media: formData.media || [],
      variants: formData.variants || [],
      variantCombinations: formData.variantCombinations || [],
      data: dataObj,
      hsn_sac_code: formData.hsn_sac_code || formData.hsnCode || null,
      tax_rate: formData.taxRate !== undefined && formData.taxRate !== '' ? parseFloat(formData.taxRate) : null,
      // Vendor assignment (admin only — backend resolves vendor_name)
      vendor_id: formData.vendor_id || null,
      // Universal Catalog Listing — bundle/package/menu/accommodation mode
      catalog_mode: formData.catalog_mode || null,
      // Service behaviour template fields (top-level for indexing)
      template: formData.template || null,
      booking_mode: formData.bookingMode || null,
      pricing_model: formData.pricingModel || null,
      // Secondary categories for multi-collection Shopify placement
      secondary_categories: formData.secondary_categories || [],
      // iter-347 — Legal & Compliance top-level fields
      country_of_origin: formData.country_of_origin?.trim() || null,
      licenses_certifications: (formData.licenses_certifications || []).filter((l) => (l.number || '').trim()),
      // iter-374 — must travel with the save, otherwise a vendor-default
      // license the user just removed here silently reappears the next
      // time the vendor edits their Store Profile licenses list.
      excluded_license_types: formData.excluded_license_types || [],
      net_quantity: formData.productType !== 'digital' ? {
        value: parseFloat(formData.net_quantity_value) || null,
        unit: formData.net_quantity_unit || 'g',
      } : null,
      shelf_life: complianceConfig.enable_expiry_rules ? {
        format: complianceConfig.expiry_date_format,
        shelf_life_days: complianceConfig.expiry_date_format === 'exact_date' ? null : (parseInt(formData.shelf_life_days, 10) || null),
        expiry_date: complianceConfig.expiry_date_format === 'exact_date' ? (formData.expiry_date || null) : null,
        min_freshness_guarantee_days: parseInt(formData.freshness_guarantee_days, 10) || null,
      } : null,
      warranty: complianceConfig.enable_warranty_rules ? {
        assurance_type: formData.warranty_assurance_type || complianceConfig.default_assurance_type,
        duration_value: parseInt(formData.warranty_duration_value, 10) || null,
        duration_unit: formData.warranty_duration_unit || complianceConfig.default_warranty_duration_unit,
      } : null,
    };
  }, [formData, taxonomyAttributes, filterAttributes, schema, complianceConfig]);

  // ── Hotel Room Types must be usable immediately, not gated behind a
  // manual "Save Draft" click or a filled-in title (user feedback: "Room
  // types should be active by default and should not require or be
  // dependent on Listing Title and Saving the listing as a draft"). The
  // instant a vendor checks "This is a Hotel / Multi-Room Property" on a
  // brand-new (unsaved) Rental listing, silently create the draft in the
  // background so RoomTypesCard unlocks on its own — same create-draft
  // call the header "Save Draft" button already uses, just auto-triggered
  // and tolerant of an empty title/description.
  const autoHotelDraftInFlight = useRef(false);
  useEffect(() => {
    if (
      schema?.listingType === 'rental' &&
      formData.is_hotel &&
      !listingId &&
      !autoHotelDraftInFlight.current
    ) {
      autoHotelDraftInFlight.current = true;
      (async () => {
        try {
          const submissionData = prepareSubmissionData();
          submissionData.title = (submissionData.title || '').trim() || 'Untitled Hotel';
          submissionData.description = (submissionData.description || '').trim() || 'Hotel details coming soon.';
          const result = await cms.saveDraft(submissionData);
          if (result?.success && result.data?.id) {
            navigate(`/edit-listing/${category}/${result.data.id}`, { replace: true });
          } else {
            autoHotelDraftInFlight.current = false; // allow retry (e.g. transient error)
          }
        } catch (e) {
          autoHotelDraftInFlight.current = false;
        }
      })();
    }
  }, [schema?.listingType, formData.is_hotel, listingId, prepareSubmissionData, cms, navigate, category]);

  /**
   * Validate form - Returns true if form can be submitted
   * Critical errors block submission, warnings don't but show toast
   */
  const validateForm = useCallback(() => {
    const newErrors = {};  // Critical errors that block submission
    const newWarnings = {}; // Warnings that don't block but show in UI
    const rules = schema?.validation || {};
    
    // Standard field validation from schema rules
    Object.entries(rules).forEach(([field, rule]) => {
      const value = formData[field];
      
      // Only block on truly required fields like title
      if (rule.required && rule.critical !== false && (!value || (Array.isArray(value) && value.length === 0))) {
        newErrors[field] = rule.message || `${field} is required`;
      }
      
      if (rule.minLength && value && value.length < rule.minLength) {
        newWarnings[field] = rule.message || `${field} should be at least ${rule.minLength} characters`;
      }
      
      if (rule.min !== undefined && value !== undefined && parseFloat(value) < rule.min) {
        newWarnings[field] = rule.message || `${field} should be at least ${rule.min}`;
      }
      
      if (rule.requiredIf && formData[rule.requiredIf] && !value) {
        newWarnings[field] = rule.message || `${field} is recommended`;
      }
      
      if (rule.pattern && value) {
        const regex = new RegExp(rule.pattern);
        if (!regex.test(value)) {
          newWarnings[field] = rule.message || `${field} has invalid format`;
        }
      }
    });
    
    // ==================== RENTAL-SPECIFIC CROSS-FIELD VALIDATION ====================
    if (schema?.listingType === 'rental' || formData.listing_type === 'rental') {
      const minDur = parseInt(formData.minimumRentalDuration, 10);
      // maxAdvanceBookingDays lives in formData.availability (saved by VendorRentalCalendar)
      const maxAdv = parseInt(
        formData?.availability?.maxAdvanceBookingDays ?? formData.maxAdvanceBookingDays,
        10
      );

      if (formData.minimumRentalDuration !== undefined && formData.minimumRentalDuration !== '' && formData.minimumRentalDuration !== null) {
        if (isNaN(minDur) || minDur < 1) {
          newErrors.minimumRentalDuration = 'Minimum rental duration must be at least 1';
        }
      }
      if (!isNaN(minDur) && minDur >= 1 && !isNaN(maxAdv) && maxAdv >= 1 && minDur > maxAdv) {
        newErrors.minimumRentalDuration = 'Minimum rental duration cannot exceed maximum advance booking window';
      }
    }

    // ==================== CRITICAL VALIDATION (Blocks Submission) ====================
    
    // Title is always required
    if (!formData.title || formData.title.trim().length < 3) {
      newErrors.title = 'Product title is required (minimum 3 characters)';
    }

    // HSN/SAC Code + Tax Rate: Required ONLY when chargeTax is enabled
    // Tier 1 (Unregistered) and Tier 5/6 (Exempt) vendors don't need these
    const chargeTaxEnabled = formData.chargeTax || formData.charge_tax;
    if (chargeTaxEnabled) {
      const hsnCode = formData.hsn_sac_code || formData.hsnCode || '';
      if (!hsnCode || hsnCode.trim().length === 0) {
        newErrors.hsn_sac_code = 'HSN/SAC code is required for invoicing (4, 6, or 8 digits)';
      } else {
        const hsnValidation = validateHSNCode(hsnCode);
        if (!hsnValidation.valid) {
          newErrors.hsn_sac_code = hsnValidation.error;
        }
      }

      // Tax Rate is mandatory when charging tax
      const taxRate = formData.taxRate;
      if (taxRate === undefined || taxRate === null || taxRate === '') {
        newErrors.taxRate = 'GST tax rate is required (select from suggestions or enter manually)';
      }
    }

    // ==================== LEGAL & COMPLIANCE VALIDATION (iter-347) ====================
    // iter-362 — Admin decides whether ANY compliance rule applies to this
    // listing's resolved category/subcategory. Products always ran this
    // block; now Service/Event/etc. listings under a category the admin
    // activated compliance for (e.g. Food/Wellness needing FSSAI/CDSCO)
    // run it too — but NOT the Net Quantity check, which only makes sense
    // for an actual physical product, not a booking-type listing.
    const _complianceActivatedForCategory = !!(
      requiredLicenseType || complianceConfig.enable_expiry_rules || complianceConfig.enable_warranty_rules
    );
    if (!isTemplateMode && (schema?.listingType === 'product' || _complianceActivatedForCategory)) {
      if (!formData.country_of_origin || !formData.country_of_origin.trim()) {
        newErrors.country_of_origin = 'Country of Origin is required';
      }

      if (schema?.listingType === 'product' && formData.productType !== 'digital') {
        const netQty = parseFloat(formData.net_quantity_value);
        if (!formData.net_quantity_value || isNaN(netQty) || netQty <= 0) {
          newErrors.net_quantity_value = 'Net Quantity is required for physical products';
        }
      }

      if (requiredLicenseType) {
        const matched = (formData.licenses_certifications || []).some(
          (l) => l.type === requiredLicenseType && (l.number || '').trim()
        );
        if (!matched) {
          newErrors.licenses_certifications = `${requiredLicenseType} license number is required for this category`;
        }
      }

      if (complianceConfig.enable_expiry_rules) {
        if (complianceConfig.expiry_date_format === 'exact_date') {
          if (!formData.expiry_date) {
            newErrors.expiry_date = `${complianceConfig.expiry_legal_label} date is required for this category`;
          }
        } else {
          const shelfDays = parseInt(formData.shelf_life_days, 10);
          if (!formData.shelf_life_days || isNaN(shelfDays)) {
            newErrors.shelf_life_days = `${complianceConfig.expiry_legal_label} (shelf life in days) is required for this category`;
          }
        }
        if (formData.freshness_guarantee_days && shelfLifeFloorDays) {
          const freshness = parseInt(formData.freshness_guarantee_days, 10);
          if (!isNaN(freshness) && freshness < shelfLifeFloorDays) {
            newErrors.freshness_guarantee_days = `Freshness guarantee cannot be below the platform minimum of ${shelfLifeFloorDays} days`;
          }
        }
      }

      if (complianceConfig.enable_warranty_rules) {
        const durationValue = parseInt(formData.warranty_duration_value, 10);
        if (!formData.warranty_duration_value || isNaN(durationValue) || durationValue <= 0) {
          newErrors.warranty_duration_value = 'Warranty/Guarantee duration is required for this category';
        }
      }
    }
    
    // ==================== WARNING VALIDATION (Shows Red Border + Toast, Doesn't Block) ====================
    // iter-386 — Catalog parents have no price of their own (children own
    // pricing) and media is optional, so skip these generic Product-style
    // warnings for that listing type.
    const isCatalog = schema?.listingType === 'catalog';

    // Media/Images - recommended but not blocking
    const media = formData.media || [];
    if (media.length === 0 && !isCatalog) {
      newWarnings.media = 'At least one product image is recommended';
    }
    
    // Price validation - recommended but not blocking for variants
    // Skip for listing types that use a different price field (subscription uses monthlyPrice, event uses ticketPrice)
    const priceFieldByType = { subscription: 'unit_price', event: 'ticketPrice', rental: 'dailyRate' };
    const priceFieldName = priceFieldByType[schema?.listingType] || 'price';
    const hasVariants = formData.hasVariants || formData.has_variants;
    const price = parseFloat(formData[priceFieldName]) || 0;
    if (!hasVariants && price <= 0 && priceFieldName === 'price' && !isCatalog) {
      newWarnings.price = 'Price should be greater than 0';
    }
    
    // Variant validation - only if variants are enabled
    if (hasVariants) {
      const variants = formData.variants || [];
      const variantCombinations = formData.variantCombinations || formData.variant_combinations || [];
      
      // Check if variants are defined when hasVariants is true
      if (variants.length === 0 && variantCombinations.length === 0) {
        newWarnings.variants = 'Variants enabled but no options defined. Add variant options or disable variants.';
      }
      
      // Validate each variant has name and options
      variants.forEach((variant, index) => {
        const variantName = variant.name || variant.attribute || '';
        const options = variant.options || variant.values || [];
        
        if (!variantName || variantName.trim() === '') {
          newWarnings[`variant_${index}_name`] = `Variant ${index + 1}: Name is missing`;
        }
        
        if (!options || options.length === 0) {
          newWarnings[`variant_${index}_options`] = `Variant "${variantName || index + 1}": No options defined`;
        } else {
          // Check each option has a value
          options.forEach((opt, optIndex) => {
            const optValue = typeof opt === 'string' ? opt : (opt.value || opt.name || '');
            if (!optValue || optValue.trim() === '') {
              newWarnings[`variant_${index}_option_${optIndex}`] = `Variant "${variantName}": Option ${optIndex + 1} value is empty`;
            }
          });
        }
      });
      
      // Validate variant combinations
      if (variantCombinations.length > 0) {
        variantCombinations.forEach((combo, index) => {
          const comboOptions = combo.options || {};
          
          // Check if combination has options defined
          if (Object.keys(comboOptions).length === 0) {
            newWarnings[`combo_${index}_options`] = `Variant combination ${index + 1}: Options not defined`;
          }
        });
      }
    }
    
    // Product Options/Add-ons validation - only if enabled
    const hasProductOptions = formData.hasProductOptions || formData.has_product_options || formData.has_custom_options;
    const productOptions = formData.productOptions || formData.customOptions || formData.product_options || [];
    
    if (hasProductOptions && productOptions.length > 0) {
      productOptions.forEach((option, index) => {
        const optionName = option.name || option.title || '';
        const optionType = option.type || '';
        const optionChoices = option.choices || option.options || option.values || [];
        
        if (!optionName || optionName.trim() === '') {
          newWarnings[`productOption_${index}_name`] = `Product option ${index + 1}: Name is missing`;
        }
        
        if (!optionType) {
          newWarnings[`productOption_${index}_type`] = `Product option "${optionName || index + 1}": Type is missing`;
        }
        
        // For selection-type options, check if choices exist
        if (['select', 'radio', 'checkbox', 'dropdown', 'multi-select'].includes(optionType)) {
          if (!optionChoices || optionChoices.length === 0) {
            newWarnings[`productOption_${index}_choices`] = `Product option "${optionName}": No choices defined`;
          }
        }
      });
    }
    
    // Combine errors and warnings for display (all show red border)
    const allIssues = { ...newErrors, ...newWarnings };
    setErrors(allIssues);
    
    // Return true if no CRITICAL errors (warnings don't block)
    return Object.keys(newErrors).length === 0;
  }, [formData, schema, isTemplateMode, requiredLicenseType, complianceConfig, shelfLifeFloorDays]);
  
  /**
   * Scroll to error field with smooth animation
   */
  const scrollToErrorField = useCallback((fieldName) => {
    // Try multiple selector strategies
    const selectors = [
      `[data-testid="field-${fieldName}"]`,
      `[name="${fieldName}"]`,
      `#${fieldName}`,
      `[data-field="${fieldName}"]`
    ];
    
    let element = null;
    for (const selector of selectors) {
      element = document.querySelector(selector);
      if (element) break;
    }
    
    if (element) {
      // Scroll to element with offset for header
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
      
      // Focus the element after scroll
      setTimeout(() => {
        element.focus?.();
        // Add a brief highlight effect
        element.classList.add('error-highlight-pulse');
        setTimeout(() => {
          element.classList.remove('error-highlight-pulse');
        }, 2000);
      }, 500);
    }
  }, []);
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (action) => {
    // Validate form - returns true if no critical errors
    const canSubmit = validateForm();
    const hasWarnings = Object.keys(errors).length > 0;
    
    // If there are critical errors, block submission
    if (action === 'submit' && !canSubmit) {
      // Get first error field and scroll to it
      const errorFields = Object.keys(errors);
      if (errorFields.length > 0) {
        scrollToErrorField(errorFields[0]);
      }
      
      // Show error toast
      toast({
        title: 'Cannot submit',
        description: 'Please fix the required fields before submitting',
        variant: 'destructive',
        className: 'top-center-toast'
      });
      return;
    }
    
    // If there are warnings (but no critical errors), show toast but allow submission
    if (action === 'submit' && hasWarnings) {
      // Scroll to first warning
      const warningFields = Object.keys(errors);
      if (warningFields.length > 0) {
        scrollToErrorField(warningFields[0]);
      }
      
      // Show warning toast but don't block
      toast({
        title: 'Some fields need attention',
        description: `${warningFields.length} field(s) have missing or incomplete data. Submitting anyway...`,
        variant: 'default',
        className: 'top-center-toast'
      });
    }
    
    // ── MRP Compliance Pre-check (before API call) ──
    if (action === 'submit') {
      const _isService = ['service', 'event', 'volunteering'].includes(schema?.listingType);
      const _isDigital = formData.productType === 'digital';
      const _reqShip = formData.requiresShipping !== false && !_isService && !_isDigital;
      const _bp = parseFloat(formData.price) || 0;
      const _mrp = parseFloat(formData.mrpPrice) || 0;
      const _absorb = !!formData.absorbShipping;

      if (_reqShip && _mrp > 0 && _bp > 0) {
        const _bumped = Math.round(_bp * 1.05 * 100) / 100;
        if (_bumped > _mrp && !_absorb) {
          setMrpModal({ open: true, basePrice: _bp, bumpedPrice: _bumped, mrpPrice: _mrp });
          return;
        }
      }
    }

    setLoading(true);
    try {
      const submissionData = prepareSubmissionData();
      
      // ── Template mode: save to master_templates, not listings ────────────
      if (isTemplateMode) {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = sessionStorage.getItem('token');
        const templatePayload = {
          templateName:        templateSetup.templateName || 'Untitled Template',
          templateDescription: templateSetup.templateDescription || '',
          templateImage:       (formData.media || [])[0] || templateMeta?.templateImage || null,
          listingType:         templateSetup.listingType || schema?.listingType,
          category:            formData.category || formData.categoryName || category,
          subcategory:         formData[schema?.subcategoryField] || formData.subcategory || '',
          defaultFieldValues:  submissionData,
          variants:            formData.variants || [],
          customOptions:       formData.customOptions || [],
          templatePriority:    templateSetup.templatePriority ?? 1,
          isActive:            formData._isActive ?? true,
        };
        const method = templateId ? 'PUT' : 'POST';
        const url    = templateId
          ? `${BACKEND_URL}/api/master-catalog/templates/${templateId}`
          : `${BACKEND_URL}/api/master-catalog/templates`;
        const res = await fetch(url, {
          method,
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(templatePayload),
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Failed to save template');
        }
        const saved = await res.json();
        toast({ title: templateId ? 'Template Updated' : 'Template Saved', description: `"${saved.templateName}" is ready for vendors.` });
        if (onTemplateSave) onTemplateSave(saved);
        setLoading(false);
        return;
      }
      // ─────────────────────────────────────────────────────────────────────

      // Check tier limits before publishing (for non-admin vendors)
      const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
      if (action === 'submit' && !isAdmin && !listingId) {
        // Check if user has exceeded their listing limit
        try {
          const limitResult = await apiCall('/api/vendors/check-listing-limit', {
            method: 'POST',
            body: JSON.stringify({ listing_type: schema?.listingType })
          });
          
          if (limitResult && !limitResult.allowed) {
            // Show tier limit modal with structured error details
            setTierLimitModal({
              open: true,
              errorType: limitResult.error_type,
              message: limitResult.message || 'You have reached your listing limit',
              options: limitResult.options || {},
              currentTypes: limitResult.current_types || [],
              newType: schema?.listingType,
              typesLimit: limitResult.types_limit || 0,
              currentCount: limitResult.current_count || 0,
              limit: limitResult.limit || 0
            });
            setLoading(false);
            return;
          }
        } catch (limitCheckError) {
          console.warn('Limit check failed, proceeding with submission:', limitCheckError);
          // Continue with submission if limit check fails
        }
      }
      
      if (listingId) {
        // Update existing listing
        if (action === 'submit') {
          const result = await cms.submitForApproval(submissionData);
          if (result.success) {
            // Use backend message to determine admin-publish vs vendor-submit
            const isPublished = result.data?.auto_approved === true;
            toast({
              title: isPublished ? 'Published' : 'Submitted for Approval',
              description: result.message || `Your ${schema?.title?.toLowerCase()} has been submitted and is pending approval.`
            });
            if (localDraftKey) localStorage.removeItem(localDraftKey);
            navigate(schema?.redirectPath || '/listings');
          } else if (result.tierLimitExceeded) {
            setTierLimitModal({
              open: true,
              ...result.tierLimitData
            });
          }
        } else {
          const result = await cms.saveDraft(submissionData);
          if (result.success) {
            toast({
              title: 'Draft Saved',
              description: `Your ${schema?.title?.toLowerCase()} has been saved successfully.`
            });
            navigate(schema?.redirectPath || '/listings');
          }
        }
      } else {
        // Create new listing
        if (action === 'submit') {
          const result = await cms.submitForApproval(submissionData);
          if (result.success) {
            const isPublished = result.data?.auto_approved === true;
            toast({
              title: isPublished ? 'Published' : 'Created and Submitted',
              description: result.message || `Your ${schema?.title?.toLowerCase()} has been created and submitted for approval.`
            });
            navigate(schema?.redirectPath || '/listings');
          } else if (result.tierLimitExceeded) {
            setTierLimitModal({
              open: true,
              ...result.tierLimitData
            });
          }
        } else {
          const result = await cms.saveDraft(submissionData);
          if (result.success) {
            toast({
              title: 'Draft Created',
              description: `Your ${schema?.title?.toLowerCase()} has been saved as draft.`
            });
            // Navigate to edit the newly created listing
            if (result.data?.id) {
              navigate(`/edit-listing/${category}/${result.data.id}`);
            } else {
              navigate(schema?.redirectPath || '/listings');
            }
          }
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      
      // Check if it's a tier limit error from the backend
      if (error.error_type === 'listing_limit' || error.error_type === 'type_limit') {
        setTierLimitModal({
          open: true,
          errorType: error.error_type,
          message: error.message || 'You have reached your listing limit',
          options: error.options || {},
          currentTypes: error.current_types || [],
          newType: schema?.listingType,
          typesLimit: error.types_limit || 0,
          currentCount: error.current_count || 0,
          limit: error.limit || 0
        });
      } else if (error.error_type === 'mrp_compliance') {
        setMrpModal({
          open: true,
          basePrice: error.base_price || 0,
          bumpedPrice: error.bumped_price || 0,
          mrpPrice: error.mrp_price || 0,
        });
      } else {
        toast({
          title: 'Error',
          description: error.message || 'Failed to save listing',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
      
      // ============ AUTO-REPLICATE: Save vendor defaults on any successful save ============
      try {
        const type = schema?.listingType;
        // Universal cross-type policy baseline
        const universalPolicies = {
          cancellation_allowed: formData.cancellationAllowed,
          cancellation_window: formData.cancellationWindow,
          terms: formData.terms,
          cancellation_details: formData.cancellationPolicyDetails,
        };
        localStorage.setItem('vendor_universal_policies', JSON.stringify(universalPolicies));

        if (type === 'product') {
          localStorage.setItem('vendor_product_defaults', JSON.stringify({
            serviceableType: formData.serviceableType,
            serviceableStates: formData.serviceableStates || [],
            internationalRegions: formData.internationalRegions || [],
            cancellationAllowed: formData.cancellationAllowed,
            cancellationWindow: formData.cancellationWindow,
            cancellationTiming: formData.cancellationTiming,
            returnPolicy: formData.returnPolicy,
            returnExchangeWindow: formData.returnExchangeWindow,
          }));
        } else if (type === 'service') {
          localStorage.setItem('vendor_service_defaults', JSON.stringify({
            cancellationAllowed: formData.cancellationAllowed,
            cancellationWindow: formData.cancellationWindow,
            cancellationTiming: formData.cancellationTiming,
            terms: formData.terms,
            cancellationPolicyDetails: formData.cancellationPolicyDetails,
            serviceGuarantee: formData.serviceGuarantee,
          }));
        } else if (type === 'rental') {
          localStorage.setItem('vendor_rental_defaults', JSON.stringify({
            cancellationAllowed: formData.cancellationAllowed,
            cancellationWindow: formData.cancellationWindow,
            terms: formData.termsConditions || formData.terms,
            damage_policy: formData.damagePolicy,
          }));
        } else if (type === 'event') {
          localStorage.setItem('vendor_event_defaults', JSON.stringify({
            cancellationAllowed: formData.cancellationAllowed,
            cancellationWindow: formData.cancellationWindow,
            terms: formData.terms,
          }));
        } else if (type === 'subscription') {
          localStorage.setItem('vendor_subscription_defaults', JSON.stringify({
            cancellationAllowed: formData.cancellationAllowed,
            cancellationWindow: formData.cancellationWindow,
            terms: formData.terms,
          }));
        }
      } catch (e) { /* ignore storage errors */ }
    }
  };
  
  // Handle invalid category
  if (!schema) {
    return (
      <DashboardLayout>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="py-8">
            <div className="flex items-center justify-center gap-3">
              <AlertTriangle className="h-8 w-8 text-orange-600" />
              <div>
                <h2 className="text-lg font-semibold text-orange-900">
                  Unknown Listing Type
                </h2>
                <p className="text-orange-700">
                  The listing type &quot;{category}&quot; is not recognized.
                </p>
                <Button 
                  variant="link"
                  onClick={() => navigate('/listings')}
                  className="mt-2 p-0 text-orange-600"
                >
                  ← Back to Listings
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }
  
  // Loading state
  if (loading && listingId) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-blue-600" />
            <p className="text-gray-600">Loading {schema.title.toLowerCase()}...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  const mainColumnCards = schema.layout?.mainColumnCards || [];
  const sidebarCards    = schema.layout?.sidebarCards    || [];
  // In template mode: only keep the publishing card; strip listing-status and vendor
  const effectiveSidebarCards = isTemplateMode
    ? sidebarCards.filter(id => id === 'publishing')
    : sidebarCards;
  
  // Get category names for dropdowns
  const categoryNames = getCategoryNames();

  /**
   * SecondaryCategoriesCard — inline component for multi-collection placement
   * Lets admins/vendors tag a listing with additional CMS categories so the Shopify
   * sync can place the product in multiple collections.
   */
  const SecondaryCategoriesCard = () => {
    const [adding, setAdding] = useState(false);
    const [pickedCat, setPickedCat] = useState('');
    const [pickedSub, setPickedSub] = useState('');

    const current = formData.secondary_categories || [];
    const primaryCat = formData[schema?.categoryField] || formData.category || '';

    const availableCats = categories.filter(c => c.name !== primaryCat);
    const availableSubs = pickedCat
      ? (categories.find(c => c.name === pickedCat)?.subcategories || []).map(s => s.name)
      : [];

    const addEntry = () => {
      if (!pickedCat) return;
      const entry = pickedSub ? { category: pickedCat, subcategory: pickedSub } : { category: pickedCat };
      const exists = current.some(e => e.category === entry.category && (e.subcategory || '') === (entry.subcategory || ''));
      if (!exists) {
        handleChange('secondary_categories', [...current, entry]);
      }
      setPickedCat(''); setPickedSub(''); setAdding(false);
    };

    const removeEntry = (idx) => {
      handleChange('secondary_categories', current.filter((_, i) => i !== idx));
    };

    return (
      <Card data-testid="secondary-categories-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-gray-700">Secondary Collections</CardTitle>
          <p className="text-xs text-gray-500 mt-0.5">Place this listing in additional Shopify collections</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {current.length === 0 && !adding && (
            <p className="text-xs text-gray-400 italic">No secondary categories added</p>
          )}
          {current.map((entry, idx) => (
            <div key={`${entry.category}-${entry.subcategory || ''}-${idx}`} className="flex items-center justify-between bg-gray-50 border rounded-md px-3 py-1.5">
              <span className="text-xs text-gray-700">
                {entry.category}{entry.subcategory ? ` / ${entry.subcategory}` : ''}
              </span>
              <button
                onClick={() => removeEntry(idx)}
                className="text-gray-400 hover:text-red-500 ml-2"
                data-testid={`remove-secondary-cat-${idx}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {adding ? (
            <div className="space-y-2 pt-1">
              <select
                className="w-full text-xs border rounded-md px-2 py-1.5 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                value={pickedCat}
                onChange={e => { setPickedCat(e.target.value); setPickedSub(''); }}
                data-testid="secondary-cat-picker"
              >
                <option value="">Select category...</option>
                {availableCats.map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
              {availableSubs.length > 0 && (
                <select
                  className="w-full text-xs border rounded-md px-2 py-1.5 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  value={pickedSub}
                  onChange={e => setPickedSub(e.target.value)}
                  data-testid="secondary-sub-picker"
                >
                  <option value="">Any subcategory (optional)</option>
                  {availableSubs.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              )}
              <div className="flex gap-2">
                <button
                  onClick={addEntry}
                  disabled={!pickedCat}
                  className="flex-1 text-xs bg-blue-600 text-white rounded-md px-2 py-1.5 disabled:opacity-40 hover:bg-blue-700"
                  data-testid="confirm-add-secondary-cat"
                >
                  Add
                </button>
                <button
                  onClick={() => { setAdding(false); setPickedCat(''); setPickedSub(''); }}
                  className="flex-1 text-xs border rounded-md px-2 py-1.5 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="w-full text-xs text-blue-600 border border-blue-200 rounded-md px-2 py-1.5 hover:bg-blue-50 mt-1"
              data-testid="add-secondary-cat-btn"
            >
              + Add collection
            </button>
          )}
        </CardContent>
      </Card>
    );
  };

  // Common form content JSX
  const formContent = (
    <ErrorBoundary onReset={() => window.location.reload()}>
      <div className="space-y-6" data-testid="universal-single-page-form">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            {!isMobile && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => isTemplateMode && onTemplateCancel ? onTemplateCancel() : navigate(schema.redirectPath || -1)}
                data-testid="back-btn"
                className="border-gray-300 hover:bg-gray-100"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {isTemplateMode ? 'Templates' : 'Back'}
              </Button>
            )}
            <div>
              <h1 className={`font-bold text-gray-900 ${isMobile ? 'text-lg' : 'text-2xl'}`}>
                {isTemplateMode
                  ? (templateId
                      ? `Edit: ${templateSetup.templateName || 'Template'}`
                      : (templateSetup.templateName ? `New Template: ${templateSetup.templateName}` : 'New Master Template')
                    )
                  : (listingId ? `Edit ${schema.title}` : `Create ${schema.title}`)
                }
              </h1>
              {autoSaveStatus.lastSaved && (
                <p className="text-xs text-gray-500">
                  Auto-saved at {autoSaveStatus.lastSaved.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
          
          {/* Action Buttons - Hidden on mobile, shown in sticky footer */}
          <div className="hidden sm:flex gap-2 flex-wrap">
            {isTemplateMode ? (
              <>
                {onTemplateCancel && (
                  <Button variant="outline" onClick={onTemplateCancel} data-testid="template-cancel-btn">
                    Cancel
                  </Button>
                )}
                <Button onClick={() => handleSubmit('template')} disabled={loading} data-testid="save-template-btn">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  {templateId ? 'Update Template' : 'Save Template'}
                </Button>
              </>
            ) : (
            <>
            {listingId && (
              <Button
                variant="outline"
                onClick={handleDuplicate}
                disabled={loading}
                title="Duplicate this listing as a draft"
                data-testid="duplicate-listing-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </Button>
            )}
            {listingId && formData.status === 'active' && (
              <Button
                variant="outline"
                onClick={handleDeactivate}
                disabled={loading}
                title="Take this listing down without deleting it — reactivate it anytime"
                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                data-testid="deactivate-listing-btn"
              >
                <EyeOff className="h-4 w-4 mr-2" />
                Deactivate
              </Button>
            )}
            {listingId && formData.status === 'inactive' && (
              <Button
                variant="outline"
                onClick={handleReactivate}
                disabled={loading}
                title="Make this listing live again — no re-approval needed"
                className="text-green-700 border-green-300 hover:bg-green-50"
                data-testid="reactivate-listing-btn"
              >
                <Eye className="h-4 w-4 mr-2" />
                Reactivate
              </Button>
            )}
            {listingId && schema?.listingType === 'catalog' && (
              <Button
                variant="outline"
                onClick={() => setShowCatalogPreview(true)}
                title="Preview how this catalog will look to customers"
                data-testid="catalog-preview-btn"
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleSubmit('draft')}
              disabled={loading || formData.revision_status === 'pending_revision_approval'}
              data-testid="save-draft-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {formData.status === 'active' ? 'Save Changes' : 'Save Draft'}
            </Button>
            {/* KYC Gate: show informational banner for pending vendors — submit button
                remains enabled so the backend can respond with a clear error if needed */}
            {['pending', 'PENDING_ONBOARDING', 'pending_onboarding'].includes(user?.status) && user?.role === 'vendor' && (
              <div className="w-full mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-center gap-2">
                <span className="shrink-0">⏳</span>
                Your account is pending admin approval. You can save drafts but cannot publish until approved.
              </div>
            )}
            {/* Submit / Publish button */}
            <Button
              onClick={() => handleSubmit('submit')}
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              {/* Admin sees "Publish" instead of "Submit for Approval" */}
              {(user?.role === 'admin' || user?.role === 'super_admin') 
                ? (formData.status === 'active' ? 'Update & Publish' : 'Publish')
                : formData.revision_status === 'pending_revision_approval' 
                  ? 'Re-submit for Approval'
                  : formData.status === 'active' 
                    ? 'Submit Changes' 
                    : 'Submit for Approval'}
            </Button>
            </>
            )} {/* end !isTemplateMode */}
          </div>
        </div>
          
          {/* Status Badge */}
          {/* Status Badges with Revision Info */}
          {(formData.status || cms.getDisplayStatus?.()) && (
            <div className="flex flex-wrap gap-2 items-center">
              {(() => {
                const displayStatus = cms.getDisplayStatus?.() || { label: formData.status, variant: 'secondary' };
                return (
                  <>
                    <Badge
                      variant={displayStatus.variant || 'secondary'}
                      className={formData.status === 'inactive' ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' : ''}
                    >
                      {displayStatus.label || formData.status}
                    </Badge>
                    {displayStatus.description && (
                      <span className="text-sm text-gray-500">{displayStatus.description}</span>
                    )}
                    {displayStatus.hasRevision && formData.revision_status === 'pending_revision_approval' && (
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                        Pending Review
                      </Badge>
                    )}
                    {formData.rejection_reason && (
                      <span className="text-sm text-red-500">
                        Rejected: {formData.rejection_reason}
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          )}
          
          {/* Draft Restore Banner — shown when a localStorage draft exists for a new listing */}
          {localDraft && !listingId && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 flex items-center justify-between" data-testid="draft-restore-banner">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  You have an unsaved draft from {new Date(localDraft.savedAt).toLocaleString()}. Would you like to restore it?
                </p>
              </div>
              <div className="flex items-center gap-2 ml-3 shrink-0">
                <button onClick={restoreLocalDraft} className="text-xs font-medium text-blue-700 hover:text-blue-900 underline">Restore</button>
                <button onClick={discardLocalDraft} className="text-blue-400 hover:text-blue-600"><X className="h-4 w-4" /></button>
              </div>
            </div>
          )}

          {/* Admin Warning: Pending vendor revision will be overridden on publish */}
          {(user?.role === 'admin' || user?.role === 'super_admin') &&
            (formData.revision_status === 'pending_revision_approval' || formData.revision_status === 'revising') && (
            <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 flex items-start gap-3" data-testid="admin-override-revision-warning">
              <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900 text-sm">Pending vendor revision will be overridden</h3>
                <p className="text-sm text-orange-800 mt-1">
                  This listing has a pending revision submitted by the vendor. When you click <strong>Update &amp; Publish</strong>, your changes become the live values and the vendor's pending revision will be discarded.
                </p>
              </div>
            </div>
          )}

          {/* Smart Defaults Applied Banner */}
          {presetsAppliedFor && ['service', 'rental'].includes(schema?.listingType) && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 flex items-center justify-between" data-testid="presets-applied-banner">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800">
                  Smart defaults applied for <strong>{presetsAppliedFor}</strong> — review and publish in under a minute.
                </p>
              </div>
              <button onClick={() => setPresetsAppliedFor(null)} className="text-green-500 hover:text-green-700 ml-3">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Validation Issues Summary Banner */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="validation-errors-banner">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800 mb-2">
                    {Object.keys(errors).length} field{Object.keys(errors).length > 1 ? 's' : ''} need attention:
                  </h3>
                  <ul className="space-y-1 text-sm text-amber-700">
                    {Object.entries(errors).slice(0, 8).map(([field, message]) => (
                      <li 
                        key={field} 
                        className="flex items-start gap-2 cursor-pointer hover:text-amber-900"
                        onClick={() => scrollToErrorField(field)}
                      >
                        <span className="text-amber-400">•</span>
                        <span className="underline">{message}</span>
                      </li>
                    ))}
                    {Object.keys(errors).length > 8 && (
                      <li className="text-amber-600 font-medium mt-2">
                        ... and {Object.keys(errors).length - 8} more issues
                      </li>
                    )}
                  </ul>
                </div>
                <button
                  onClick={() => setErrors({})}
                  className="text-red-400 hover:text-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Column (2/3) */}
            <div className="lg:col-span-2 space-y-6">
              {mainColumnCards.map((cardId, cardIdx) => (
                <SchemaCardRenderer
                  key={cardId}
                  cardId={cardId}
                  schema={schema}
                  formData={formData}
                  errors={errors}
                  categories={categoryNames}
                  subcategories={currentSubcategories}
                  taxonomyAttributes={taxonomyAttributes}
                  customWorkflow={customWorkflow}
                  handleChange={handleChange}
                  handleTaxonomyAttributeChange={handleTaxonomyAttributeChange}
                  isTemplateMode={isTemplateMode}
                  templateSetup={templateSetup}
                  complianceConfig={complianceConfig}
                  requiredLicenseType={requiredLicenseType}
                  shelfLifeFloorDays={shelfLifeFloorDays}
                  shelfLifeGuaranteeWording={shelfLifeGuaranteeWording}
                  defaultLicenses={defaultLicenses}
                  listingId={listingId}
                  onSaveDraftClick={() => handleSubmit('draft')}
                  saveDraftLoading={loading}
                  onTemplateSetupChange={(field, value) => {
                    if (field === 'listingType') {
                      setTemplateSetup(prev => ({ ...prev, listingType: value }));
                      setFormData({});
                    } else {
                      setTemplateSetup(prev => ({ ...prev, [field]: value }));
                    }
                  }}
                  insertBeforeTagInput={cardId === 'basicInfo' ? (
                    <FilterAttributesCard
                      categoryId={formData.categoryId}
                      subcategoryId={formData.subcategoryId}
                      productTitle={formData.title || formData.name || ''}
                      values={filterAttributes}
                      onChange={setFilterAttributes}
                      embedded={true}
                      categoryNameHint={formData[schema.categoryField] || formData.category || ''}
                    />
                  ) : null}
                />
              ))}

              {/* Custom Workflow Section */}
              {customWorkflow && schema.features?.hasCustomWorkflow && (
                <Card>
                  <CardHeader>
                    <CardTitle>Additional Information</CardTitle>
                    <CardDescription>
                      Category-specific requirements
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <WorkflowFieldRenderer
                      workflow={customWorkflow}
                      formData={formData}
                      handleChange={handleChange}
                      errors={errors}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Return Claim Eligibility — shown for physical products in listing mode */}
              {!isTemplateMode && schema?.listingType === 'product' && formData.productType !== 'digital' && (
                <ReturnClaimEligibilityCard formData={formData} setFormData={setFormData} />
              )}

              {/* Hyperlocal Order Window — in the main column for correct layout */}
              {!isTemplateMode && (formData.delivery_modes || []).includes('hyperlocal') && (
                <Card data-testid="hyperlocal-availability-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">
                      Hyperlocal Order Window & Holidays
                    </CardTitle>
                    <p className="text-xs text-gray-500">
                      Same-Day delivery is only available during these hours. Outside the window, customers see the next available slot instead of a price.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <AvailabilityScheduler
                      availability={formData.hyperlocal_availability || { business_hours: [], holiday_dates: [] }}
                      onChange={(av) => setFormData(prev => ({ ...prev, hyperlocal_availability: av }))}
                      hideSchedulingSettings={true}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Sidebar Column (1/3) */}
            <div className="space-y-6">
              {effectiveSidebarCards.map((cardId) => (
                <SchemaCardRenderer
                  key={cardId}
                  cardId={cardId}
                  schema={schema}
                  formData={formData}
                  errors={errors}
                  categories={categoryNames}
                  subcategories={currentSubcategories}
                  taxonomyAttributes={taxonomyAttributes}
                  handleChange={handleChange}
                  handleTaxonomyAttributeChange={handleTaxonomyAttributeChange}
                  isSidebar={true}
                  isTemplateMode={isTemplateMode}
                />
              ))}
              
              {/* Vendor Card - hidden in template mode */}
              {!isTemplateMode && (user?.role === 'admin' || user?.role === 'super_admin') && (
                <VendorCard
                  formData={formData}
                  handleChange={handleChange}
                />
              )}

              {/* Shipping Pricing Card — shows 5% contribution & Aniyor Listing Price */}
              {!isTemplateMode && (
                <ShippingPricingCard
                  formData={formData}
                  listingType={schema?.listingType}
                  handleChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
                />
              )}

              {/* Delivery Available Card — only for physical/shippable listing types.
                  Services, Events, Subscriptions, Volunteering do NOT ship. 
                  Rentals manage delivery at the schema-field level (deliveryAvailable per template). */}
              {!isTemplateMode && ['product', 'classified'].includes(
                (schema?.listingType || '').toLowerCase()
              ) && (
                <Card data-testid="delivery-modes-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      Delivery Available
                      <span
                        title="Controls which delivery options appear on your listing at checkout. Domestic covers all India via our integrated carrier network (respects your serviceability zones). Hyperlocal is fast same-day delivery within a 45 km service radius."
                        className="cursor-help text-gray-400 hover:text-gray-600"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </span>
                    </CardTitle>
                    <p className="text-xs text-gray-500">Select the delivery modes available for this listing.</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      {
                        key: 'domestic',
                        label: 'Domestic',
                        desc: 'All India delivery via our integrated carrier network. Respects your vendor serviceability zones, city areas & radius settings.',
                      },
                      {
                        key: 'hyperlocal',
                        label: 'Hyperlocal (Same-Day)',
                        desc: 'Fast same-day delivery within a 45 km service radius. Ideal for restaurants, tiffin services, groceries, flowers, medicines, bakery items and other time-sensitive or perishable products. Max weight up to 12 kg and max box size 44 cm × 42 cm × 40 cm.',
                      },
                      {
                        key: 'international',
                        label: 'International',
                        desc: 'Cross-border delivery. Rates & availability depend on your carrier configuration.',
                      },
                    ].map(({ key, label, desc }) => {
                      const modes = formData.delivery_modes || ['domestic'];
                      const checked = modes.includes(key);
                      // For hyperlocal, check city eligibility
                      const isHyperlocalBlocked = key === 'hyperlocal'
                        && quickCityEligibility.checked
                        && !quickCityEligibility.eligible;
                      const toggle = () => {
                        if (isHyperlocalBlocked) return; // city not supported
                        const next = checked
                          ? modes.filter(m => m !== key)
                          : [...modes, key];
                        setFormData(prev => ({
                          ...prev,
                          delivery_modes: next.length ? next : ['domestic'],
                        }));
                      };
                      return (
                        <div key={key} data-testid={`delivery-mode-${key}`}>
                          <label
                            className={`flex items-start gap-3 ${isHyperlocalBlocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer group'}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={toggle}
                              disabled={isHyperlocalBlocked}
                              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer disabled:cursor-not-allowed"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-800">{label}</span>
                              <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                            </div>
                          </label>
                          {isHyperlocalBlocked && (
                            <div className="mt-1.5 ml-7 flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2" data-testid="hyperlocal-city-blocked-notice">
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                              <p className="text-xs text-amber-800">
                                Same-Day delivery is currently unavailable
                                {quickCityEligibility.city ? ` in ${quickCityEligibility.city}` : ' for your city'}.{' '}
                                Covered cities: Delhi NCR, Mumbai, Bengaluru, Chennai, Ahmedabad, Hyderabad, Pune, Jaipur, Kolkata.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Exclusive hyperlocal notice */}
                    {(formData.delivery_modes || []).length === 1 &&
                      formData.delivery_modes[0] === 'hyperlocal' && (
                      <div className="mt-2 rounded-md bg-orange-50 border border-orange-200 p-2 flex items-start gap-2">
                        <Info className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-orange-700">
                          <strong>Exclusively Hyperlocal:</strong> Standard and Express shipping options
                          will be hidden at checkout. Only Same-Day delivery will be shown.
                        </p>
                      </div>
                    )}
                    {/* TAT display for hyperlocal */}
                    {(formData.delivery_modes || []).includes('hyperlocal') && (
                      <p className="text-xs text-green-700 bg-green-50 rounded px-2 py-1 mt-1">
                        Hyperlocal TAT shown to customers: "Typically delivers within 15–45 mins"
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Hyperlocal Prep Time — dedicated card when Same-Day is selected */}
              {!isTemplateMode && (formData.delivery_modes || []).includes('hyperlocal') && (
                <HyperlocalPrepTimeCard formData={formData} setFormData={setFormData} />
              )}

              {/* Secondary Collections Card - shown in listing mode when categories are loaded */}
              {!isTemplateMode && categories.length > 0 && (
                <SecondaryCategoriesCard />
              )}
            </div>
          </div>
          
          {/* Sticky Footer for Mobile/Tablet */}
          <div className="sm:hidden fixed bottom-20 left-0 right-0 bg-white border-t p-3 flex gap-2 justify-end z-40 shadow-lg">
            {isTemplateMode ? (
              <Button size="sm" onClick={() => handleSubmit('template')} disabled={loading} className="flex-1" data-testid="save-template-mobile-btn">
                <Save className="h-4 w-4 mr-1" />
                {templateId ? 'Update' : 'Save Template'}
              </Button>
            ) : (
              <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSubmit('draft')}
              disabled={loading || formData.revision_status === 'pending_revision_approval'}
              className="flex-1"
            >
              <Save className="h-4 w-4 mr-1" />
              {formData.status === 'active' ? 'Save' : 'Draft'}
            </Button>
            {['pending', 'PENDING_ONBOARDING', 'pending_onboarding'].includes(user?.status) && user?.role === 'vendor' ? (
              <Button
                size="sm"
                disabled
                className="flex-1 opacity-60 cursor-not-allowed"
                data-testid="submit-btn-mobile-disabled"
              >
                <Eye className="h-4 w-4 mr-1" />
                Pending KYC
              </Button>
            ) : (
            <Button
              size="sm"
              onClick={() => handleSubmit('submit')}
              disabled={loading}
              className="flex-1"
            >
              <Eye className="h-4 w-4 mr-1" />
              {formData.revision_status === 'pending_revision_approval' ? 'Re-submit' : 'Submit'}
            </Button>
            )}
              </>
            )}
          </div>
          
          {/* Bottom padding for sticky footer on mobile */}
          <div className="sm:hidden h-24" />
        </div>
        
        {/* MRP Compliance Modal */}
        {mrpModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="mrp-compliance-modal-overlay">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" data-testid="mrp-compliance-modal">
              <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-center gap-3">
                <div className="bg-amber-100 rounded-full p-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-900 text-base">Action Required: MRP Compliance</h3>
                  <p className="text-xs text-amber-700 mt-0.5">Legal ceiling check failed</p>
                </div>
              </div>
              <div className="px-6 py-5 space-y-4">
                <p className="text-sm text-slate-700 leading-relaxed">
                  Your listing price <strong className="text-slate-900">(₹{mrpModal.bumpedPrice?.toLocaleString('en-IN')})</strong> after
                  the 5% shipping contribution exceeds the MRP ceiling of <strong className="text-slate-900">₹{mrpModal.mrpPrice?.toLocaleString('en-IN')}</strong>.
                </p>
                <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Base Price</span><span className="font-medium">₹{mrpModal.basePrice?.toLocaleString('en-IN')}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">+ Shipping Pool (5%)</span><span className="font-medium">₹{((mrpModal.bumpedPrice || 0) - (mrpModal.basePrice || 0)).toLocaleString('en-IN')}</span></div>
                  <div className="border-t border-slate-200 pt-1.5 flex justify-between"><span className="text-slate-500">Listing Price</span><span className="font-semibold text-red-600">₹{mrpModal.bumpedPrice?.toLocaleString('en-IN')}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">MRP Ceiling</span><span className="font-semibold text-slate-800">₹{mrpModal.mrpPrice?.toLocaleString('en-IN')}</span></div>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  To comply, enable <strong>"Absorb Shipping Contribution"</strong> in the Pricing Breakdown panel. Your listing price will be capped at MRP and the platform payout will be calculated from the shadow base price.
                </p>
              </div>
              <div className="px-6 py-4 border-t bg-slate-50 flex gap-3">
                <button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, absorbShipping: true }));
                    setMrpModal({ open: false, basePrice: 0, bumpedPrice: 0, mrpPrice: 0 });
                    toast({ title: 'Shipping absorbed', description: 'Listing price will now be capped at MRP. You can submit again.' });
                  }}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors"
                  data-testid="mrp-absorb-btn"
                >
                  Absorb Shipping Contribution
                </button>
                <button
                  onClick={() => setMrpModal({ open: false, basePrice: 0, bumpedPrice: 0, mrpPrice: 0 })}
                  className="px-4 py-2.5 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 rounded-lg transition-colors"
                  data-testid="mrp-modal-close-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tier Limit Modal for subscription-based publishing */}
        <TierLimitModal
          open={tierLimitModal.open}
          onOpenChange={(isOpen) => setTierLimitModal(prev => ({ ...prev, open: isOpen }))}
          errorType={tierLimitModal.errorType}
          message={tierLimitModal.message}
          options={tierLimitModal.options}
          currentTypes={tierLimitModal.currentTypes}
          newType={tierLimitModal.newType}
          typesLimit={tierLimitModal.typesLimit}
          currentCount={tierLimitModal.currentCount}
          limit={tierLimitModal.limit}
          onSwitchComplete={() => {
            setTierLimitModal(prev => ({ ...prev, open: false }));
            // Refresh or retry submission
          }}
          onAddonPurchased={() => {
            setTierLimitModal(prev => ({ ...prev, open: false }));
            // Retry submission after addon purchase
          }}
        />
        {schema?.listingType === 'catalog' && (
          <CatalogPreviewModal
            open={showCatalogPreview}
            onOpenChange={setShowCatalogPreview}
            listingId={listingId}
            formData={formData}
          />
        )}
      </ErrorBoundary>
  );
  
  // Wrap with appropriate layout based on context
  if (noLayout) {
    return formContent;
  }

  if (isMobile && user?.role === 'vendor') {
    return (
      <LiteLayout 
        title={listingId ? `Edit ${schema?.title || 'Listing'}` : `New ${schema?.title || 'Listing'}`}
        showBackButton={true}
        onBack={() => navigate('/listings')}
      >
        {formContent}
      </LiteLayout>
    );
  }
  
  return (
    <DashboardLayout>
      {formContent}
    </DashboardLayout>
  );
};

export default UniversalSinglePageForm;
